import React from 'react';
import './MesasList.scss';
import { Salon, Mesa, PedidoGroup } from '../../../Configuracion/types';
import { formatCurrency } from '../../../../../utils/functions';
import { DisplayPrefs } from '../types';
import { FaUser, FaClock } from 'react-icons/fa';
import PedidoGroupsList from '../PedidoGroupsList/PedidoGroupsList';
import { usePedidoGroups } from '../hooks/usePedidoGroups';

interface MesasListProps {
    salones: Salon[];                 // list of salones to source mesas from (flattened if > 1)
    selectedSalon?: Salon;            // optional: restrict to a single salon
    onSelectMesa: (mesa: Mesa) => void; // click handler to open MesaController
    onSelectGroup?: (group: PedidoGroup) => void; // click handler to open MesaController/Sales for group
    selectedGroup?: PedidoGroup | null; // currently selected group
    displayPrefs?: DisplayPrefs;
    hideGroups?: boolean;             // hide the groups section (for Ver Todos grid)
    onlyGroups?: boolean;             // only show groups section (for global groups container)
}

const MesasList: React.FC<MesasListProps> = ({ salones, selectedSalon, onSelectMesa, onSelectGroup, selectedGroup, displayPrefs, hideGroups = false, onlyGroups = false }) => {
    const [timePassed, setTimePassed] = React.useState<Record<number, string>>({});

    // Only enable pedido groups fetching when groups are actually being displayed
    const shouldFetchGroups = !hideGroups || onlyGroups;

    // Pedido groups hook - conditionally enabled
    const {
        pedidoGroups,
        loading: groupsLoading,
        creating: groupsCreating,
        newlyCreatedGroupId,
        handleCreateGroup,
        handleDeleteGroup,
        clearNewlyCreatedFlag,
    } = usePedidoGroups({ enabled: shouldFetchGroups });

    const mesas: Mesa[] = React.useMemo(() => {
        console.log('[MesasList] useMemo triggered - salones:', salones.length, 'selectedSalon:', selectedSalon?.salon_name);
        
        // CRITICAL FIX: Always get fresh mesas from salones array, not from stale selectedSalon object
        // selectedSalon is used only to filter which salon, but we get the actual mesas from salones
        const list: Mesa[] = selectedSalon
            ? (salones.find(s => s.salon_id === selectedSalon.salon_id)?.mesas as unknown as Mesa[] || [])
            : salones.flatMap((s) => (s.mesas as unknown as Mesa[]));
        
        // Log the state of each mesa BEFORE sorting
        list.forEach((m, idx) => {
            if (m.id === 47 || m.num === '3') {
                console.log(`[MesasList] useMemo - Mesa ${m.id} (num ${m.num}): used=${m.used}, charged=${(m as any).charged}`);
            }
        });
        
        // sort ascending by mesa.num (defensive coercion in case num is string)
        const sorted = [...list].sort((a, b) => Number(a?.num ?? 0) - Number(b?.num ?? 0));
        console.log('[MesasList] Recalculating mesas, count:', sorted.length, 'salones:', salones.length);
        return sorted;
    }, [salones, selectedSalon]);

    // Group mesas by salon when in "Ver Todos" mode (no selectedSalon)
    const mesasBySalon = React.useMemo(() => {
        if (selectedSalon) return null; // Single salon, no grouping needed
        
        const grouped = salones.map(salon => ({
            salon_id: salon.salon_id,
            salon_name: salon.salon_name,
            mesas: (salon.mesas as unknown as Mesa[]).sort((a, b) => Number(a?.num ?? 0) - Number(b?.num ?? 0))
        }));
        console.log('[MesasList] Recalculating mesasBySalon, salons:', grouped.length);
        return grouped;
    }, [salones, selectedSalon]);

    const formatTimeElapsed = (timeUsed: string) => {
        if (!timeUsed) return '';
        const now = Date.now();
        const usedAt = new Date(timeUsed).getTime();
        if (isNaN(usedAt)) return '';
        let diff = now - usedAt;
        const sign = diff < 0 ? '-' : '';
        diff = Math.abs(diff);
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `${sign}${hours} H ${minutes} M`;
    };

    // Update time elapsed every minute
    React.useEffect(() => {
        const interval = window.setInterval(() => {
            const updated: Record<number, string> = {};
            mesas.forEach(mesa => {
                if (mesa.used && (mesa as any).time_used) {
                    updated[mesa.id] = formatTimeElapsed((mesa as any).time_used);
                }
            });
            setTimePassed(updated);
        }, 60_000);
        return () => clearInterval(interval);
    }, [mesas]);

    // Clear newly created flag when component unmounts (user navigated away)
    React.useEffect(() => {
        return () => {
            clearNewlyCreatedFlag();
        };
    }, [clearNewlyCreatedFlag]);

    return (
        <div className="mesas-list-container">
            {/* Mesas Section - Hide if onlyGroups is true */}
            {!onlyGroups && (
                <>
                    {/* Ver Todos mode - Group by salon */}
                    {mesasBySalon ? (
                        <div className="mesas-by-salon">
                            {mesasBySalon.map((salonGroup) => (
                                <div key={salonGroup.salon_id} className="salon-group">
                                    <div className="salon-group__header">
                                        <h3 className="salon-group__title">{salonGroup.salon_name}</h3>
                                        <span className="salon-group__count">
                                            {salonGroup.mesas.length} {salonGroup.mesas.length === 1 ? 'mesa' : 'mesas'}
                                        </span>
                                    </div>
                                    <div className="mesas-list">
                                        {salonGroup.mesas.map((mesa) => {
                                            const hasTag = (mesa as any).tag && (mesa as any).tag !== '';
                                            const mesaNum = mesa.num;
                                            const mesaTag = hasTag ? (mesa as any).tag : null;
                                            const isVirtual = (mesa as any).is_virtual === true;
                                            const isDisabled = mesa.state === false;
                                            const stateClass = mesa.used ? 'used' : (isDisabled ? 'disabled' : 'free');
                                            const title = hasTag
                                                ? `Mesa ${mesaNum} (${mesaTag})${mesa.used ? ' — ocupada' : ''}${isDisabled ? ' — BLOQUEADA' : ''}${isVirtual ? ' — VIRTUAL (Delivery/Pickup)' : ''}`
                                                : `Mesa ${mesaNum}${mesa.used ? ' — ocupada' : ''}${isDisabled ? ' — BLOQUEADA' : ''}${isVirtual ? ' — VIRTUAL (Delivery/Pickup)' : ''}`;
                                            const totalCharges = (mesa as any).total_charges as number | undefined;
                                            const timeUsed = (mesa as any).time_used as string | undefined;
                                            const currentPeople = (mesa as any).current_people as number | undefined;
                                            const attendantName = (mesa as any).attendant_name as string | undefined;
                                            
                                            return (
                                                <button
                                                    key={`${mesa.id}-${mesa.used}-${mesa.charged}`}
                                                    className={`mesa-pill ${stateClass} ${hasTag ? 'has-tag' : ''} ${typeof totalCharges === 'number' && totalCharges > 0 ? 'with-charges' : ''} ${isVirtual ? 'virtual-mesa' : ''}`}
                                                    title={title}
                                                    onClick={() => {
                                                        if (isDisabled) {
                                                            alert('Esta mesa está bloqueada y no puede recibir pedidos');
                                                            return;
                                                        }
                                                        onSelectMesa(mesa);
                                                    }}
                                                >
                                                    <div className="mesa-pill__content">
                                                        <div className="mesa-pill__header">
                                                            {isVirtual ? (
                                                                <span className="mesa-pill__tag mesa-pill__tag--primary">MV</span>
                                                            ) : displayPrefs?.showTag !== false ? (
                                                                <>
                                                                    {hasTag ? (
                                                                        <>
                                                                            <span className="mesa-pill__tag mesa-pill__tag--primary">{mesaTag}</span>
                                                                            {displayPrefs?.showNumber !== false && (
                                                                                <span className="mesa-pill__num mesa-pill__num--secondary">{mesaNum}</span>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        // If the mesa has no custom tag, show the number as a fallback.
                                                                        // Keep showing the number when either "showNumber" is enabled
                                                                        // or when "showTag" is enabled but the specific mesa lacks a tag
                                                                        // (this makes list mode behave like the map mode fallback).
                                                                        ((displayPrefs?.showNumber ?? true) || (displayPrefs?.showTag ?? false)) ? (
                                                                            <span className="mesa-pill__num">{mesaNum}</span>
                                                                        ) : null
                                                                    )}
                                                                </>
                                                            ) : (
                                                                displayPrefs?.showNumber !== false ? (
                                                                    <span className="mesa-pill__num">{mesaNum}</span>
                                                                ) : null
                                                            )}
                                                            {displayPrefs?.showCharges !== false && typeof totalCharges === 'number' && totalCharges > 0 && (
                                                                <span className="mesa-pill__charges" aria-label="Total cobrado">
                                                                    {formatCurrency(totalCharges)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {mesa.used && (
                                                            <div className="mesa-pill__details">
                                                                {displayPrefs?.showWaiter !== false && attendantName && (
                                                                    <span className="mesa-pill__attendant" title={attendantName}>
                                                                        {attendantName.split(' ').map(n => n[0]).join('')}
                                                                    </span>
                                                                )}
                                                                
                                                                {displayPrefs?.showPeopleType === 'current' ? (
                                                                    currentPeople && currentPeople > 0 ? (
                                                                        <span className="mesa-pill__people">
                                                                            <FaUser className="mesa-pill__icon" />
                                                                            {currentPeople}
                                                                        </span>
                                                                    ) : null
                                                                ) : (
                                                                    <span className="mesa-pill__people">
                                                                        <FaUser className="mesa-pill__icon" />
                                                                        {mesa.people}
                                                                    </span>
                                                                )}
                                                                
                                                                {displayPrefs?.showElapsed !== false && timeUsed && (
                                                                    <span className="mesa-pill__time">
                                                                        <FaClock className="mesa-pill__icon" />
                                                                        {timePassed[mesa.id] ?? formatTimeElapsed(timeUsed)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Single salon mode - No grouping */
                        <div className="mesas-list">
                            {mesas.map((mesa) => {
                                const hasTag = (mesa as any).tag && (mesa as any).tag !== '';
                                const mesaNum = mesa.num;
                                const mesaTag = hasTag ? (mesa as any).tag : null;
                                const isVirtual = (mesa as any).is_virtual === true;
                                const isDisabled = mesa.state === false;
                                const stateClass = mesa.used ? 'used' : (isDisabled ? 'disabled' : 'free');
                                
                                console.log(`[MesasList] Rendering mesa ${mesa.id} (${mesaNum}): used=${mesa.used}, charged=${(mesa as any).charged}, stateClass=${stateClass}`);
                                
                                const title = hasTag
                                    ? `Mesa ${mesaNum} (${mesaTag})${mesa.used ? ' — ocupada' : ''}${isDisabled ? ' — BLOQUEADA' : ''}${isVirtual ? ' — VIRTUAL (Delivery/Pickup)' : ''}`
                                    : `Mesa ${mesaNum}${mesa.used ? ' — ocupada' : ''}${isDisabled ? ' — BLOQUEADA' : ''}${isVirtual ? ' — VIRTUAL (Delivery/Pickup)' : ''}`;
                                const totalCharges = (mesa as any).total_charges as number | undefined;
                                const timeUsed = (mesa as any).time_used as string | undefined;
                                const currentPeople = (mesa as any).current_people as number | undefined;
                                const attendantName = (mesa as any).attendant_name as string | undefined;
                                
                                return (
                                        <button
                                                key={`${mesa.id}-${mesa.used}-${mesa.charged}`}
                                                className={`mesa-pill ${stateClass} ${hasTag ? 'has-tag' : ''} ${typeof totalCharges === 'number' && totalCharges > 0 ? 'with-charges' : ''} ${isVirtual ? 'virtual-mesa' : ''}`}
                                                title={title}
                                                onClick={() => {
                                                    if (isDisabled) {
                                                        alert('Esta mesa está bloqueada y no puede recibir pedidos');
                                                        return;
                                                    }
                                                    onSelectMesa(mesa);
                                                }}
                                        >
                                                <div className="mesa-pill__content">
                                                    <div className="mesa-pill__header">
                                                        {isVirtual ? (
                                                            // Virtual mesa always shows "MV" explicitly
                                                            <span className="mesa-pill__tag mesa-pill__tag--primary">MV</span>
                                                        ) : displayPrefs?.showTag !== false ? (
                                                            // When showTag preference is enabled
                                                            <>
                                                                {hasTag ? (
                                                                    // Has custom tag: show tag as primary
                                                                    <>
                                                                        <span className="mesa-pill__tag mesa-pill__tag--primary">{mesaTag}</span>
                                                                        {displayPrefs?.showNumber !== false && (
                                                                            <span className="mesa-pill__num mesa-pill__num--secondary">{mesaNum}</span>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    // No custom tag: show number as fallback. This will show the
                                                                    // number when either showNumber is enabled or when showTag is
                                                                    // enabled but the mesa doesn't have a tag (consistent with map view).
                                                                    ((displayPrefs?.showNumber ?? true) || (displayPrefs?.showTag ?? false)) ? (
                                                                        <span className="mesa-pill__num">{mesaNum}</span>
                                                                    ) : null
                                                                )}
                                                            </>
                                                        ) : (
                                                            // When showTag is false: show number only (if enabled)
                                                            displayPrefs?.showNumber !== false ? (
                                                                <span className="mesa-pill__num">{mesaNum}</span>
                                                            ) : null
                                                        )}
                                                        {displayPrefs?.showCharges !== false && typeof totalCharges === 'number' && totalCharges > 0 && (
                                                            <span className="mesa-pill__charges" aria-label="Total cobrado">
                                                                {formatCurrency(totalCharges)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {mesa.used && (
                                                        <div className="mesa-pill__details">
                                                            {/* Attendant Name */}
                                                            {displayPrefs?.showWaiter !== false && attendantName && (
                                                                <span className="mesa-pill__attendant" title={attendantName}>
                                                                    {attendantName.split(' ').map(n => n[0]).join('')}
                                                                </span>
                                                            )}
                                                            
                                                            {/* People Count */}
                                                            {displayPrefs?.showPeopleType === 'current' ? (
                                                                currentPeople && currentPeople > 0 ? (
                                                                    <span className="mesa-pill__people">
                                                                        <FaUser className="mesa-pill__icon" />
                                                                        {currentPeople}
                                                                    </span>
                                                                ) : null
                                                            ) : (
                                                                <span className="mesa-pill__people">
                                                                    <FaUser className="mesa-pill__icon" />
                                                                    {mesa.people}
                                                                </span>
                                                            )}
                                                            
                                                            {/* Time Elapsed */}
                                                            {displayPrefs?.showElapsed !== false && timeUsed && (
                                                                <span className="mesa-pill__time">
                                                                    <FaClock className="mesa-pill__icon" />
                                                                    {timePassed[mesa.id] ?? formatTimeElapsed(timeUsed)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                        </button>
                                );
                        })}
                </div>
                    )}
                </>
            )}

            {/* Pedido Groups Section - Below Tables, hide if hideGroups is true */}
            {!hideGroups && (
                <div className="groups-section">
                <PedidoGroupsList
                    pedidoGroups={pedidoGroups}
                    selectedGroup={selectedGroup}
                    onSelectGroup={(group) => {
                        // Clear the newly created flag when selecting any group
                        clearNewlyCreatedFlag();
                        onSelectGroup?.(group);
                    }}
                    onCreateGroup={handleCreateGroup}
                    onDeleteGroup={handleDeleteGroup}
                    creating={groupsCreating}
                    loading={groupsLoading}
                    newlyCreatedGroupId={newlyCreatedGroupId}
                />
                </div>
            )}
        </div>
    );
};

export default MesasList;